import React, { FC, useCallback, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import axiosInstant from '../../api';
import {
  expertTeamSingleProEndpoint,
  singleProEndpoint,
  publicProForProject,
} from '../../api/endpoints';
import { useUser } from '../../hooks/useUser';
import { isInternal } from '../../utils';
import { ISinglePro } from './state';
import SingleExpert from './SingleExpert';
import ProfileHeaderBar from './ProfileHeaderBar';
import ProfileSkeleton from './ProfileSkeleton';
import ProfileNotFound from './ProfileNotFound';
import { setPageTitle } from '../PageTitle';

interface Props {
  isSharePage?: boolean;
}

const SingleProProfile: FC<Props> = ({ isSharePage }) => {
  const [searchParams] = useSearchParams();
  const { role } = useUser();

  const proId = searchParams.get('proId');
  const projectId = searchParams.get('project');
  const token = searchParams.get('token');

  const [expert, setExpert] = useState<ISinglePro>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // True when the request actually carried project context, i.e. the payload's
  // bio/key points were substituted server-side with project-scoped values.
  const projectScoped = isSharePage
    ? !!(token && projectId)
    : isInternal(role) && !!projectId;

  const getProProfileData = useCallback(async () => {
    if (!proId) {
      setExpert(undefined);
      setError(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(false);
    let url: string;
    if (isSharePage) {
      url =
        token && projectId
          ? publicProForProject(token, projectId, proId)
          : singleProEndpoint(proId);
    } else if (isInternal(role)) {
      url = projectId
        ? `${expertTeamSingleProEndpoint(proId)}?project=${projectId}`
        : expertTeamSingleProEndpoint(proId);
    } else {
      url = singleProEndpoint(proId);
    }
    try {
      const { data } = await axiosInstant.get(url);
      if (data) {
        setExpert(data);
        setPageTitle(data?.displayName);
      } else {
        setExpert(undefined);
        setError(true);
      }
    } catch (e) {
      setExpert(undefined);
      setError(true);
    }
    setLoading(false);
    // Keyed on the params that change the payload — NOT the whole searchParams,
    // so videos-section filter/page changes no longer refetch the profile.
  }, [proId, projectId, token, isSharePage, role]);

  useEffect(() => {
    getProProfileData();
  }, [getProProfileData]);

  // Query-only transitions (brand listing -> profile) never trigger the app's
  // pathname-keyed scroll reset, so the profile used to mount below the fold.
  useEffect(() => {
    if (proId) window.scrollTo(0, 0);
  }, [proId]);

  let content;
  if (loading) {
    content = <ProfileSkeleton />;
  } else if (error) {
    content = <ProfileNotFound isSharePage={isSharePage} onRetry={getProProfileData} />;
  } else {
    content = (
      <SingleExpert
        expert={expert}
        isSharePage={isSharePage}
        projectId={projectId}
        projectScoped={projectScoped}
      />
    );
  }

  return (
    <Box className="bg-page min-h-[70vh] flex flex-col gap-[24px] pt-[40px] pb-[40px]">
      {!loading && !error && <ProfileHeaderBar expert={expert} isSharePage={isSharePage} />}
      {content}
    </Box>
  );
};

export default SingleProProfile;
